































































































































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { Banner } from "@scrile/api-provider/dist/api/BannersProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ProfileLink } from "@scrile/api-provider/dist/api/ProfileLinksProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { VipStatus } from "@scrile/api-provider/dist/api/UserProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import useRole from "@/hooks/useRole";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { timeAgo } from "@/lib/time";
import { resolveUserName } from "@/lib/userHelpers";
import { isGetUserMediaSupported, isWebRTCSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import { AuthUserAuthorities, BaseMenuItem, LoginSignUpModalData } from "@/types";
import { EVENTS } from "@/const";
import BannerItem from "@/components/BannerItem";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import BaseMenu from "@/components/BaseMenu";
import BaseTooltip from "@/components/BaseTooltip";
import UnfollowConfirm from "@/components/UnfollowConfirm";
import UserFollowSubscribeModal from "@/components/UserFollowSubscribeModal";
import ConfirmPurchaseProfileLink from "@/views/PageProfile/components/ConfirmPurchaseProfileLink";
import ProfileHeader from "../ProfileHeader";
import ProfileStatistic from "../ProfileStatistic";
import QuestionaryInfoPrinter from "../QuestionaryInfoPrinter";

export default defineComponent({
  name: "ProfileInfoView",
  components: {
    ConfirmPurchaseProfileLink,
    BaseTooltip,
    BannerItem,
    UnfollowConfirm,
    QuestionaryInfoPrinter,
    ProfileHeader,
    BaseButton,
    BaseMenu,
    BaseIcon,
    BaseConfirm,
    ProfileStatistic,
    UserFollowSubscribeModal,
  },
  props: {
    user: { type: Object as () => UserPublicData, required: true },
    questionaryInfo: { type: Object as () => Record<string, any>, default: () => ({}) },
    isFollowing: { type: Boolean, required: true },
    isBlocked: { type: Boolean, required: false },
    isAuthor: { type: Boolean, required: true },
    subscriptionLevels: { type: Array as () => SubscriptionLevel[], required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    activeSubscriptions: { type: Array as () => Subscription[], required: true },
    hasContentCount: { type: Boolean, required: true },
    verifiedIconSize: { type: [Number, String], required: false, default: 16 },
    isBookingAvailable: { type: Boolean, required: true },
    isCallable: { type: Boolean, required: true },
    banners: { type: Array as () => Banner[], default: () => [] },
    profileLinks: { type: Array as () => ProfileLink[], default: () => [] },
    hasServices: { type: Boolean, required: true },
    loadingCheckServices: { type: Boolean, required: true },
  },
  setup(props, { emit, root }) {
    const showFollowSubscribe = ref(false);
    const showUnfollowConfirm = ref(false);
    const linkForPurchase = ref<ProfileLink | null>(null);

    const screenName = computed<string>(() => resolveUserName(props.user));
    const username = computed<string>(() => props.user.username);
    const verified = computed<boolean>(() => props.user.verified);
    const isVip = computed(() => props.user.vipStatus !== VipStatus.DEFAULT);

    const hasSubscriptionLevels = computed<boolean>(() => props.subscriptionLevels.length > 0);
    const showOnlineStatus = computed<boolean>(() => config.featureUserOnlineStatus);

    const canUseWebRTC = isWebRTCSupported() && isGetUserMediaSupported();

    const { authorities, authUser, updateUser, isAdmin, isCreator } = useAuthUser();
    const { isCreator: isUserCreator } = useRole(computed(() => props.user));

    const canEditInfo = computed<boolean>(() => {
      const requiredAuthorities: AuthUserAuthorities = props.isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      return checkAuthorities(authorities.value, requiredAuthorities);
    });

    const canEditServices = computed(() => {
      return checkAuthorities(
        authorities.value,
        props.isAuthor ? ["updateMyCreatorService"] : ["updateAnyCreatorService"]
      );
    });

    const bannerToShow = computed(() => props.banners[0]);

    const hasActiveSubscription = computed(() => props.activeSubscriptions.length > 0);
    const subscribeButtonText = computed<string>(() => {
      if (hasActiveSubscription.value) {
        return $t("Subscribed for {0}", [$t("{0}/mo", [formatMoneyString(props.activeSubscriptions[0].amount)])]);
      }
      const availableSubscriptions = props.subscriptionLevels.filter(i => (i.contentCount ?? 0) > 0);
      const cheapestAvailableSubscription = availableSubscriptions.reduce<SubscriptionLevel | null>((out, item) => {
        if (!out) {
          return item;
        }
        if (!item.pricePackage) {
          return out;
        }
        if (item.pricePackage.amount < (out.pricePackage?.amount ?? Infinity)) {
          return item;
        }
        return out;
      }, null);
      if (!cheapestAvailableSubscription || !cheapestAvailableSubscription.pricePackage) {
        return $t("No available subscriptions");
      }
      return $t("Subscribe from {0}", [
        $t("{0}/mo", [formatMoneyString(cheapestAvailableSubscription.pricePackage.amount)]),
      ]);
    });

    const isModeVisibility = computed(() => isCreator.value && config.featureInvisibleMode && props.isAuthor);

    const lastOnline = computed(() => {
      if (props.user.onlineStatus?.isOnline && !props.user.onlineStatus?.invisible) return $t("Online");
      if (props.user.onlineStatus?.isOnline && props.user.onlineStatus?.invisible && isModeVisibility.value)
        return $t("Invisible");
      if (!props.user.onlineStatus?.lastOnlineTime) return "";
      return $t(`Last visited {0}`, [timeAgo(props.user.onlineStatus.lastOnlineTime, { addSuffix: true })]);
    });

    const activeUserMode = ref(lastOnline.value);

    const userModes = computed<BaseMenuItem[]>(() => [
      {
        text: $t("Online"),
        name: "Online",
        class: activeUserMode.value === "Online" ? "fw-6oo" : "",
      },
      {
        text: $t("Invisible"),
        name: "Invisible",
        class: activeUserMode.value === "Invisible" ? "fw-6oo" : "",
      },
    ]);

    const isDisabled = computed<boolean>(() => props.user.disabled ?? true);
    const showActionButton = computed<boolean>(() => {
      return !isDisabled.value && !props.isBlocked && !props.isAuthor;
    });
    const showSubscribeButton = computed<boolean>(() => {
      return showActionButton.value && hasSubscriptionLevels.value && props.hasContentCount;
    });
    const showAppointmentButton = computed<boolean>(() => {
      return showActionButton.value && props.isBookingAvailable;
    });

    const servicesButtonText = computed<string>(() => {
      return props.hasServices ? $t("My services") : $t("Add service");
    });

    const showUnlockServicesButton = computed<boolean>(() => {
      return showActionButton.value && !props.isAuthor && props.hasServices;
    });

    const onSignUpSuccess = async () => {
      await providers.FollowingsProvider.toggle({ id: props.user.id });
      emit("onFollowUpdate", !props.isFollowing);
    };
    const loginSignUpModalData: LoginSignUpModalData = { onSignUpSuccess };
    const onUnfollowClick = () => {
      onSignUpSuccess();
      showUnfollowConfirm.value = false;
    };
    const onFollowingClick = useAuthorisedOnly(() => onSignUpSuccess(), loginSignUpModalData);
    const onSubscribingClick = useAuthorisedOnly(() => (showFollowSubscribe.value = true), loginSignUpModalData);

    const sendMessage = useAuthorisedOnly(async () => {
      await root.$router.push({ name: "messages", params: { userId: props.user.id } });
    }, loginSignUpModalData);

    const handleFollow = () => {
      if (!props.isFollowing) {
        onFollowingClick();
      } else {
        showUnfollowConfirm.value = true;
      }
    };

    const handleCall = useAuthorisedOnly(() => {
      if (canUseWebRTC) {
        emitter.emit(EVENTS.CALL_OUTGOING, { opponentUser: props.user });
      } else {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("This browser does not support WebRTC") });
      }
    });

    const onSelectMode = (value: string) => {
      activeUserMode.value = value;
      const invisible = value === "Invisible";
      providers.OnlineStatusProvider.updateVisibility({ data: { invisible } });
      authUser.value && updateUser(authUser.value);
      emit("updateUser");
    };

    return {
      bannerToShow,
      username,
      verified,
      lastOnline,
      showFollowSubscribe,
      hasActiveSubscription,
      screenName,
      canEditInfo,
      showOnlineStatus,
      showUnfollowConfirm,
      showActionButton,
      showSubscribeButton,
      showAppointmentButton,
      subscribeButtonText,
      userModes,
      activeUserMode,
      isModeVisibility,
      isAdmin,
      isVip,
      linkForPurchase,
      canEditServices,
      servicesButtonText,
      showUnlockServicesButton,
      isUserCreator,
      sendMessage,
      onFollowingClick,
      onSubscribingClick,
      handleFollow,
      onUnfollowClick,
      onSelectMode,
      handleCall,
      VipStatus,
    };
  },
});
